/** The texts of labeling profiles (LBL-05 to LBL-07), spread into the app's dictionaries (`lib/i18n.tsx`). */
export const labelingEn = {
  labelingProfiles: 'Labeling profiles',
  labelingProfile: 'Labeling profile',
  labelingProfilesIntro: 'A profile sets the questions asked first for each label, the controls on a box, and which labels start locked or hidden, or stay locked. It never takes a label or tool away: everything else stays answerable in the details, readers unlock or show what they need, and a label kept locked opens under another profile.',
  standardProfile: 'Standard',
  standardProfileHint: 'Every attribute of every label, as the catalog defines it',
  jobProfile: 'Job',
  jobProfileHint: 'The job asks for {name}. Finishing an image checks its answers, whichever profile you use.',
  editProfiles: 'Edit labeling profiles…',
  profileScope_personal: 'Mine',
  profileScope_site: 'Site',
  profileScope_platform: 'Platform',
  newProfile: 'New profile',
  copyProfile: 'Copy',
  copyOf: 'Copy of {name}',
  archiveProfile: 'Archive',
  archiveProfileConfirm: 'Archive {name}? Jobs keep the revision they use.',
  useAsDefault: 'Use as my default',
  myDefault: 'My default',
  profileRevision: 'Revision {revision}',
  profileWithRevision: '{name}, revision {revision}',
  profileOwner: 'By {name}',
  profileName: 'Name',
  profileFor: 'For',
  profileSaved: 'Saved as revision {revision}.',
  asStandard: 'as Standard',
  profileLabels: 'Labels of this profile',
  standardReadOnly: 'Standard asks what the label catalog defines and cannot be changed. Copy it to make a profile of your own.',
  profileReadOnly: 'You cannot change this profile. Copy it to make one of your own.',
  resetToStandard: 'Reset to Standard',
  customized: 'Customized',
  asStandardHint: 'As in Standard until you change something here.',
  catalogOrder: 'Back to the catalog’s order',
  unsavedChanges: 'Unsaved changes',
  discardProfileChanges: 'Drop the changes to this profile?',
  offeredAttributes: 'Asked for',
  noOtherAttributes: 'No other attributes',
  inWorkspace: 'In the workspace',
  objectLocking: 'Objects',
  lockMode_unlocked: 'Start unlocked',
  lockMode_start: 'Start locked',
  lockMode_always: 'Always locked',
  alwaysLockedHint: 'Readers change these objects only under another profile, as for crop boxes while reading fractures.',
  startHidden: 'Start hidden',
  startsLocked: 'locked',
  alwaysLocked: 'always locked',
  startsHidden: 'hidden',
  requiredAnswer: 'Required',
  boxTop: 'Above the box',
  boxBottom: 'Below the box',
  boxNone: 'Nothing',
  defaultAnswer: 'Default',
  unanswered: 'Unanswered',
  moreAttributes: 'More attributes',
  newAttribute: 'New attribute…',
  previewProfile: 'Preview',
  previewHint: 'A box of this label as readers see it',
  defaultLabelingProfileHint: 'Its studies open with this profile, and jobs created from it start with it.',
  numericToleranceHint: 'Empty: one step of the attribute',
  jobProfileFieldHint: 'What the job asks for. Readers may switch profiles; finishing an image checks this one.',
}

export const labelingDe: Record<keyof typeof labelingEn, string> = {
  labelingProfiles: 'Labeling-Profile',
  labelingProfile: 'Labeling-Profil',
  labelingProfilesIntro: 'Ein Profil legt fest, welche Fragen je Label zuerst gestellt werden, welche Regler am Rahmen stehen und welche Labels gesperrt oder ausgeblendet starten oder gesperrt bleiben. Es nimmt nie ein Label oder Werkzeug weg: alles andere bleibt in den Details beantwortbar, Lesende entsperren oder zeigen, was sie brauchen, und ein gesperrt gehaltenes Label öffnet sich unter einem anderen Profil.',
  standardProfile: 'Standard',
  standardProfileHint: 'Alle Attribute aller Labels, wie der Katalog sie festlegt',
  jobProfile: 'Auftrag',
  jobProfileHint: 'Der Auftrag fragt nach {name}. Beim Abschließen eines Bildes werden dessen Antworten geprüft, mit welchem Profil Sie auch arbeiten.',
  editProfiles: 'Labeling-Profile bearbeiten…',
  profileScope_personal: 'Meine',
  profileScope_site: 'Standort',
  profileScope_platform: 'Plattform',
  newProfile: 'Neues Profil',
  copyProfile: 'Kopieren',
  copyOf: 'Kopie von {name}',
  archiveProfile: 'Archivieren',
  archiveProfileConfirm: '{name} archivieren? Aufträge behalten die Revision, die sie verwenden.',
  useAsDefault: 'Als meinen Standard verwenden',
  myDefault: 'Mein Standard',
  profileRevision: 'Revision {revision}',
  profileWithRevision: '{name}, Revision {revision}',
  profileOwner: 'Von {name}',
  profileName: 'Name',
  profileFor: 'Für',
  profileSaved: 'Als Revision {revision} gespeichert.',
  asStandard: 'wie Standard',
  profileLabels: 'Labels dieses Profils',
  standardReadOnly: 'Standard fragt, was der Labelkatalog festlegt, und lässt sich nicht ändern. Kopieren Sie es, um ein eigenes Profil zu erstellen.',
  profileReadOnly: 'Sie können dieses Profil nicht ändern. Kopieren Sie es, um ein eigenes zu erstellen.',
  resetToStandard: 'Auf Standard zurücksetzen',
  customized: 'Angepasst',
  asStandardHint: 'Wie in Standard, bis Sie hier etwas ändern.',
  catalogOrder: 'Zurück zur Reihenfolge des Katalogs',
  unsavedChanges: 'Ungespeicherte Änderungen',
  discardProfileChanges: 'Die Änderungen an diesem Profil verwerfen?',
  offeredAttributes: 'Gefragt',
  noOtherAttributes: 'Keine weiteren Attribute',
  inWorkspace: 'Im Arbeitsbereich',
  objectLocking: 'Objekte',
  lockMode_unlocked: 'Starten entsperrt',
  lockMode_start: 'Starten gesperrt',
  lockMode_always: 'Immer gesperrt',
  alwaysLockedHint: 'Lesende ändern diese Objekte nur unter einem anderen Profil, etwa Zuschnitt-Rechtecke beim Befunden von Frakturen.',
  startHidden: 'Startet ausgeblendet',
  startsLocked: 'gesperrt',
  alwaysLocked: 'immer gesperrt',
  startsHidden: 'ausgeblendet',
  requiredAnswer: 'Pflicht',
  boxTop: 'Über dem Rahmen',
  boxBottom: 'Unter dem Rahmen',
  boxNone: 'Nichts',
  defaultAnswer: 'Vorgabe',
  unanswered: 'Unbeantwortet',
  moreAttributes: 'Weitere Attribute',
  newAttribute: 'Neues Attribut…',
  previewProfile: 'Vorschau',
  previewHint: 'Ein Rahmen dieses Labels, wie Lesende ihn sehen',
  defaultLabelingProfileHint: 'Ihre Studien öffnen mit diesem Profil, und daraus erstellte Aufträge beginnen damit.',
  numericToleranceHint: 'Leer: ein Schritt des Attributs',
  jobProfileFieldHint: 'Wonach der Auftrag fragt. Lesende können das Profil wechseln; beim Abschließen eines Bildes wird dieses geprüft.',
}
